// Trade Log Component - Real-time trade history

import { Trade } from '../lib/types';
import { useTradingStore } from '../lib/store';
import { format } from 'date-fns';

interface TradeLogProps {
  maxTrades?: number;
}

export function TradeLog({ maxTrades = 20 }: TradeLogProps) {
  const strategyA = useTradingStore((state) => state.strategyA);
  const strategyB = useTradingStore((state) => state.strategyB);
  const simulateResolution = useTradingStore((state) => state.simulateResolution);
  const config = useTradingStore((state) => state.config);
  
  // Combine and sort trades by timestamp (most recent first)
  const allTrades = [...strategyA.trades, ...strategyB.trades]
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    .slice(0, maxTrades);

  if (allTrades.length === 0) {
    return (
      <div className="trade-log empty">
        <h3>📋 Trade Log</h3>
        <p className="empty-message">No trades yet. Start the simulator to see trades appear here.</p>
      </div>
    );
  }

  return (
    <div className="trade-log">
      <h3>📋 Trade Log ({allTrades.length} trades)</h3>
      
      <div className="trade-list">
        {allTrades.map((trade) => (
          <TradeRow 
            key={trade.id} 
            trade={trade} 
            onSimulateResolve={config.mode === 'SIMULATION' ? simulateResolution : undefined}
          />
        ))}
      </div>
    </div>
  );
}

interface TradeRowProps {
  trade: Trade;
  onSimulateResolve?: (tradeId: string) => void;
}

function TradeRow({ trade, onSimulateResolve }: TradeRowProps) {
  const isPending = trade.result === 'pending';
  const isWin = trade.result === 'win';
  
  return (
    <div className={`trade-row ${trade.result}`}>
      <div className="trade-strategy">
        <span className={`badge badge-${trade.strategy}`}>
          {trade.strategy}
        </span>
      </div>
      
      <div className="trade-info">
        <div className="trade-ticker">{trade.ticker}</div>
        <div className="trade-title" title={trade.title}>
          {trade.title.length > 40 ? trade.title.slice(0, 40) + '...' : trade.title}
        </div>
      </div>
      
      <div className="trade-details">
        <div className="trade-price">
          <span className="label">Price:</span>
          <span className="value">{trade.marketPrice}¢</span>
        </div>
        <div className="trade-bet">
          <span className="label">Bet:</span>
          <span className={`value bet-${trade.betDirection.toLowerCase()}`}>
            {trade.betDirection}
          </span>
        </div>
        <div className="trade-cost">
          <span className="label">Cost:</span>
          <span className="value">${trade.cost.toFixed(3)}</span>
        </div>
      </div>
      
      <div className="trade-result">
        {isPending ? (
          <>
            <span className="pending-badge">PENDING</span>
            {onSimulateResolve && (
              <button 
                className="btn-simulate"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onSimulateResolve(trade.id);
                }}
                title="Simulate resolution"
              >
                🎲
              </button>
            )}
          </>
        ) : (
          <span className={`pnl ${isWin ? 'profit' : 'loss'}`}>
            {trade.pnl !== undefined && (
              <>
                {trade.pnl >= 0 ? '+' : ''}${trade.pnl.toFixed(3)}
              </>
            )}
          </span>
        )}
      </div>
      
      <div className="trade-time">
        {format(new Date(trade.timestamp), 'HH:mm:ss')}
      </div>
    </div>
  );
}
